import * as React from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import styled from 'styled-components';
import { FieldErrors, FieldValues, useController, useFormContext } from 'react-hook-form';
import { format } from 'date-fns/format';
import { isValid, isAfter } from 'date-fns';
import { FilledTextFieldProps } from '@mui/material';
import { generateValidations, MaterialUiFieldProps } from '../../core/form';
import { getCurrentLocaleFromUserOfficeCountry, getLocalizedWord } from '../../lib/utils';
import { PrintableInputWrapper, StyledInput } from './Input';
import { aboveCommonElementsZIndex } from '../../appComponents/cssConstants';
import { hasValue } from '../../lib/lib';

const DateField = (props: Props) => {
	const {
		label,
		readOnly,
		name,
		minDate = minValidDate,
		maxDate,
		fullWidth = false,
		displayInLineLabel = true
	} = props;
	const {
		formState: { errors }
	} = useFormContext();

	const validate = (value: string) => {
		const date = new Date(value);
		if (minDate && date < minDate) return 'The selected date is before the minimum date';
		if (maxDate && date > maxDate) return 'The selected date is past the maximum date';
		return true;
	};
	const { field } = useController({ name, rules: generateValidations({ ...props, validate }) });
	const value = field.value ? new Date(field.value) : null;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleChange = (date: any) => {
		const isValidDate = isValid(date) && isAfter(date, minValidDate);
		field.onChange(isValidDate ? format(date, getLocalizedWord('dateFormat')) : date);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getCurrentLocaleFromUserOfficeCountry()}>
			<StyledDatePicker
				enableAccessibleFieldDOMStructure={false}
				name={name}
				label={label}
				value={value}
				maxDate={maxDate}
				minDate={minDate}
				onChange={handleChange}
				readOnly={readOnly}
				slots={{
					textField: CustomInput
				}}
				slotProps={{
					textField: { fullWidth, displayInLineLabel, errors } as unknown
				}}
			/>
		</LocalizationProvider>
	);
};

export default DateField;

interface FieldCommonProps {
	fullWidth?: boolean;
	displayInLineLabel?: boolean;
}

interface Props extends FieldCommonProps, MaterialUiFieldProps {
	minDate?: Date;
	maxDate?: Date;
}

// Set a mininum valid date after the 4 digits year 1000, so that handleChange won't format invaid date during user typing.
const minValidDate = new Date(1000, 1, 1);

const CustomInput = (props: CustomInputProps) => {
	const { name, fullWidth = false, displayInLineLabel = true, errors, ...params } = props;

	const hasError = hasValue(errors[name]);

	return (
		<PrintableInputWrapper $fullWidth={fullWidth} $displayInLineLabel={displayInLineLabel}>
			<StyledInput
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...params}
				variant="filled"
				size="small"
				placeholder={getLocalizedWord('dateFormat')}
				$displayInLineLabel={displayInLineLabel}
				InputProps={{
					...params.InputProps,
					disableUnderline: true
				}}
				error={hasError}
				helperText={errors[name]?.message as string}
			/>
		</PrintableInputWrapper>
	);
};

interface CustomInputProps extends FieldCommonProps, FilledTextFieldProps {
	errors: FieldErrors<FieldValues>;
}

const StyledDatePicker = styled(DatePicker)`
	z-index: ${aboveCommonElementsZIndex};
	margin-bottom: 10px;
`;
